import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { PageBody, PageHeader } from "../../components/Layout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../components/ui/dialog";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import ShipPlatformDialog from "../../components/ShipPlatformDialog";
import {
    ArrowLeft, FloppyDisk, Sparkle, Trash, Plus, X,
    InstagramLogo, FacebookLogo, LinkedinLogo, ThreadsLogo, YoutubeLogo,
    FilmStrip, ImageSquare, FileText, VideoCamera,
    Brain, Calendar, PaperPlaneTilt, Check, Envelope, Quotes, ChatCircle,
    Warning, ArrowRight, ShieldCheck, ShieldWarning, ShieldSlash,
    ArrowsClockwise,
} from "@phosphor-icons/react";

const ASSET_META = {
    Instagram:    { label: "Instagram",   Icon: InstagramLogo },
    Facebook:     { label: "Facebook",    Icon: FacebookLogo },
    LinkedIn:     { label: "LinkedIn",    Icon: LinkedinLogo },
    Threads:      { label: "Threads",     Icon: ThreadsLogo },
    Carousel:     { label: "Carousel",    Icon: ImageSquare },
    VideoScript:  { label: "Video Script", Icon: VideoCamera },
    YouTube:      { label: "YouTube",     Icon: YoutubeLogo },
    Blog:         { label: "Blog",        Icon: FileText },
    Email:        { label: "Email",       Icon: Envelope },
    QuoteGraphic: { label: "Quote Graphic", Icon: Quotes },
    TextGraphic:  { label: "Text Graphic",  Icon: ChatCircle },
};

export default function SourceDetail() {
    const { id } = useParams();
    const nav = useNavigate();
    const [source, setSource] = useState(null);
    const [opts, setOpts] = useState({ source_types: [], audiences: [], content_pillars: [], content_goals: [], asset_types: [], visual_styles: [], asset_statuses: [] });
    const [assets, setAssets] = useState([]);
    const [saving, setSaving] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [selectedPlatforms, setSelectedPlatforms] = useState(new Set());
    const [goalOverride, setGoalOverride] = useState("");
    const [analyzing, setAnalyzing] = useState(false);
    const [analysisResult, setAnalysisResult] = useState(null);
    const [scheduleDialog, setScheduleDialog] = useState(null);
    const [autoRunning, setAutoRunning] = useState(false);
    const [shipBusy, setShipBusy] = useState(false);

    const fetchAll = async () => {
        const [optR, srcR, assetsR] = await Promise.all([
            api.get("/content-studio/sources/options"),
            api.get(`/content-studio/sources/${id}`),
            api.get(`/content-studio/sources/${id}/assets`),
        ]);
        return { opts: optR.data, source: srcR.data, assets: assetsR.data || [] };
    };
    const reload = async () => {
        try {
            const r = await fetchAll();
            setOpts(r.opts);
            setSource(r.source);
            setAssets(r.assets);
        } catch {
            toast.error("Source not found");
        }
    };

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const r = await fetchAll();
                if (cancelled) return;
                setOpts(r.opts);
                setSource(r.source);
                setAssets(r.assets);
            } catch {
                if (!cancelled) toast.error("Source not found");
            }
        })();
        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const patchSource = async (patch) => {
        setSaving(true);
        try {
            const r = await api.patch(`/content-studio/sources/${id}`, patch);
            setSource(r.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const togglePlatform = (p) => {
        setSelectedPlatforms(prev => {
            const next = new Set(prev);
            if (next.has(p)) next.delete(p); else next.add(p);
            return next;
        });
    };

    const generate = async () => {
        const platforms = Array.from(selectedPlatforms);
        if (platforms.length === 0) { toast.error("Pick at least one asset type"); return; }
        setGenerating(true);
        const t = toast.loading(`Generating ${platforms.length} asset${platforms.length === 1 ? "" : "s"}…`);
        try {
            const body = { platforms };
            if (goalOverride) body.content_goal = goalOverride;
            const r = await api.post(`/content-studio/sources/${id}/generate-assets`, body);
            const { created, errors } = r.data;
            toast.dismiss(t);
            toast.success(`${created.length} asset${created.length === 1 ? "" : "s"} generated${errors.length ? ` · ${errors.length} failed` : ""}`);
            if (errors.length) {
                errors.forEach(e => toast.error(`${e.asset_type}: ${e.error}`, { duration: 6000 }));
            }
            setSelectedPlatforms(new Set());
            await reload();
        } catch (e) {
            toast.dismiss(t);
            toast.error(e?.response?.data?.detail || "Generation failed");
        } finally {
            setGenerating(false);
        }
    };

    const deleteAsset = async (assetId) => {
        if (!window.confirm("Delete this generated asset?")) return;
        await api.delete(`/content-studio/content/${assetId}`);
        toast.success("Deleted");
        await reload();
    };

    const updateAsset = async (assetId, patch) => {
        await api.patch(`/content-studio/content/${assetId}`, patch);
        await reload();
    };

    // V2 — Asset Analysis Engine
    const runAnalysis = async () => {
        setAnalyzing(true);
        try {
            const r = await api.post(`/content-studio/sources/${id}/analyze`);
            setAnalysisResult(r.data);
            if (r.data.insufficient_context) {
                toast.warning("Source too short", { description: r.data.warning, duration: 8000 });
            } else {
                // pre-select recommended platforms
                const recs = (r.data.recommendations || []).filter(x => x.recommended).map(x => x.asset_type);
                setSelectedPlatforms(new Set(recs));
                toast.success(`Analysis complete — ${recs.length} asset type${recs.length === 1 ? "" : "s"} recommended`);
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Analysis failed");
        } finally {
            setAnalyzing(false);
        }
    };

    // V2.5 — Auto-Pipeline: analyze → generate all recommended → leave at Draft
    const runAutoPipeline = async () => {
        setAutoRunning(true);
        const t = toast.loading("Running auto-pipeline — analyzing then generating recommended assets…");
        try {
            const r = await api.post(`/content-studio/sources/${id}/auto-pipeline`, {});
            toast.dismiss(t);
            const generated = r.data.generated?.length || 0;
            const errs = r.data.errors?.length || 0;
            toast.success(`${generated} asset${generated === 1 ? "" : "s"} generated${errs ? ` · ${errs} failed` : ""}`, {
                description: "Review them at Production Line",
                action: { label: "Open Production Line", onClick: () => { localStorage.setItem("haevn-cs-tab", "production-line"); window.location.href = "/content-studio"; } },
                duration: 10000,
            });
            setAnalysisResult(r.data.analysis);
            await reload();
        } catch (e) {
            toast.dismiss(t);
            toast.error(e?.response?.data?.detail || "Auto-pipeline failed");
        } finally {
            setAutoRunning(false);
        }
    };

    // V2.9.2 — Ship now opens the platform-picker first.
    const [shipDialogOpen, setShipDialogOpen] = useState(false);

    // The actual ship worker, invoked from ShipPlatformDialog's confirm.
    const shipSourceToBuffer = async (platforms) => {
        setShipBusy(true);
        setShipDialogOpen(false);
        const t = toast.loading("Shipping — starting…");
        try {
            const r = await api.post(`/content-studio/sources/${id}/ship`, {
                platforms: platforms && platforms.length ? platforms : null,
            });
            const jobId = r.data?.job_id;
            if (!jobId) {
                toast.error("Ship start failed", { id: t });
                return;
            }
            // Poll every 2.5s until the job hits completed / failed. Backend
            // moved to async in V2.9.1 (sync ship exceeded Cloudflare's ~100s
            // edge timeout in prod).
            const started = Date.now();
            let job = null;
            while (true) {
                let r2;
                try { r2 = await api.get(`/content-studio/ship-jobs/${jobId}`); }
                catch { await new Promise((res) => setTimeout(res, 2500)); if (Date.now() - started > 15 * 60 * 1000) { job = { status: "failed", error: "Poll timed out." }; break; } continue; }
                job = r2.data || {};
                if (job.status === "running" || job.status === "pending") {
                    toast.loading("Shipping — generating + queueing…", { id: t });
                    await new Promise((res) => setTimeout(res, 2500));
                    if (Date.now() - started > 15 * 60 * 1000) { job = { status: "failed", error: "Ship exceeded 15 minutes." }; break; }
                    continue;
                }
                break;
            }
            if (job.status === "failed") {
                toast.error(`Ship failed: ${job.error || "unknown error"}`, { id: t, duration: 12000 });
                await reload();
                return;
            }
            const d = job.summary || {};
            const parts = [`${d.generated || 0} assets generated`];
            if (d.queued) parts.push(`${d.queued} queued to Buffer`);
            if (d.blocked_by_validator) parts.push(`${d.blocked_by_validator} blocked by fact-check`);
            if (d.skipped_media_required) parts.push(`${d.skipped_media_required} skipped — need media`);
            if ((d.queue_errors || []).length) parts.push(`${d.queue_errors.length} queue errors`);
            const msg = parts.join(" · ");
            if (d.analysis_warning) {
                toast.warning(d.analysis_warning, { id: t, duration: 9000 });
            } else if (d.queued) {
                toast.success(msg, {
                    id: t, duration: 9000,
                    description: "Open Production Line to review.",
                    action: { label: "Open Production Line", onClick: () => { localStorage.setItem("haevn-cs-tab", "production-line"); window.location.href = "/content-studio"; } },
                });
            } else {
                toast.warning(msg, { id: t, duration: 9000 });
            }
            await reload();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Ship failed", { id: t });
        } finally {
            setShipBusy(false);
        }
    };

    // V2 — asset workflow transitions
    // When the backend returns 409 (fact-validator soft-block) we prompt
    // the operator for an override reason and replay the call. This is
    // the soft-block contract: needs_review never silently passes, but
    // a single click + reason gets it through.
    const transition = async (assetId, newStatus, overrideReason = null) => {
        try {
            const body = overrideReason
                ? { status: newStatus, override_reason: overrideReason }
                : { status: newStatus };
            await api.post(`/content-studio/content/${assetId}/transition`, body);
            toast.success(overrideReason ? `Moved to ${newStatus} (override)` : `Moved to ${newStatus}`);
            await reload();
        } catch (e) {
            const status = e?.response?.status;
            if (status === 409 && !overrideReason) {
                const reason = window.prompt(
                    "This asset has unsupported claims flagged by the fact validator. "
                    + "Provide an override reason to approve it anyway:",
                );
                if (!reason || !reason.trim()) {
                    toast.error("Approval cancelled — override reason required.");
                    return;
                }
                return transition(assetId, newStatus, reason.trim());
            }
            toast.error(e?.response?.data?.detail || "Transition failed");
        }
    };

    // V2 — Buffer queue (no specific date)
    const queueToBuffer = async (assetId, overrideReason = null) => {
        try {
            const url = overrideReason
                ? `/content-studio/content/${assetId}/queue?override_reason=${encodeURIComponent(overrideReason)}`
                : `/content-studio/content/${assetId}/queue`;
            const r = await api.post(url);
            toast.success("Queued in Buffer", { description: r.data.buffer?.message || "" });
            await reload();
        } catch (e) {
            const status = e?.response?.status;
            if (status === 409 && !overrideReason) {
                const reason = window.prompt(
                    "This asset has unsupported claims flagged by the fact validator. "
                    + "Provide an override reason to queue it anyway:",
                );
                if (!reason || !reason.trim()) {
                    toast.error("Queue cancelled — override reason required.");
                    return;
                }
                return queueToBuffer(assetId, reason.trim());
            }
            toast.error(e?.response?.data?.detail || "Queue failed");
        }
    };

    // V2.6 — re-run the fact validator on a single asset (e.g. after
    // the operator edited the copy to remove unsupported claims).
    const revalidate = async (assetId) => {
        try {
            await api.post(`/content-studio/content/${assetId}/revalidate`);
            toast.success("Re-validated");
            await reload();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Revalidation failed");
        }
    };

    if (!source) return <PageBody><p className="text-sm text-muted-foreground">Loading source…</p></PageBody>;

    return (
        <>
            <PageHeader
                overline="Content Studio · Source"
                title={source.title}
                description="The upstream content. Edit the source, then choose which platforms the AI should produce. Each asset comes back structured — slides, hashtags, chapters — not a single text blob."
                testid="source-detail-header"
                actions={
                    <Button variant="ghost" size="sm" className="rounded-sm" onClick={() => nav("/content-studio")} data-testid="back-to-studio">
                        <ArrowLeft size={14} className="mr-1" /> Back to Studio
                    </Button>
                }
            />
            <PageBody>
                {/* --- Source editor card --- */}
                <section className="border border-border bg-card rounded-sm p-6 mb-8" data-testid="source-editor">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                        <Field label="Source type">
                            <Select value={source.source_type} onValueChange={v => patchSource({ source_type: v })}>
                                <SelectTrigger data-testid="edit-type" className="rounded-sm h-9 text-xs"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {opts.source_types.map(t => (<SelectItem key={t} value={t}>{t}</SelectItem>))}
                                </SelectContent>
                            </Select>
                        </Field>
                        <Field label="Audience">
                            <Select value={source.audience || "none"} onValueChange={v => patchSource({ audience: v === "none" ? "" : v })}>
                                <SelectTrigger data-testid="edit-audience" className="rounded-sm h-9 text-xs"><SelectValue placeholder="—" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">—</SelectItem>
                                    {opts.audiences.map(t => (<SelectItem key={t} value={t}>{t}</SelectItem>))}
                                </SelectContent>
                            </Select>
                        </Field>
                        <Field label="Content pillar">
                            <Select value={source.content_pillar || "none"} onValueChange={v => patchSource({ content_pillar: v === "none" ? "" : v })}>
                                <SelectTrigger data-testid="edit-pillar" className="rounded-sm h-9 text-xs"><SelectValue placeholder="—" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">—</SelectItem>
                                    {opts.content_pillars.map(t => (<SelectItem key={t} value={t}>{t}</SelectItem>))}
                                </SelectContent>
                            </Select>
                        </Field>
                        <Field label="Content goal (default)">
                            <Select value={source.content_goal || "none"} onValueChange={v => patchSource({ content_goal: v === "none" ? "" : v })}>
                                <SelectTrigger data-testid="edit-goal" className="rounded-sm h-9 text-xs"><SelectValue placeholder="—" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">—</SelectItem>
                                    {opts.content_goals.map(t => (<SelectItem key={t} value={t}>{t}</SelectItem>))}
                                </SelectContent>
                            </Select>
                        </Field>
                    </div>
                    <Field label="Title">
                        <Input
                            data-testid="edit-title"
                            value={source.title}
                            onChange={e => setSource({ ...source, title: e.target.value })}
                            onBlur={() => source.title.trim() && patchSource({ title: source.title.trim() })}
                            className="rounded-sm"
                        />
                    </Field>
                    <div className="mt-4">
                        <Field label="Source text (transcript / body / notes)">
                            <Textarea
                                data-testid="edit-text"
                                rows={8}
                                value={source.source_text}
                                onChange={e => setSource({ ...source, source_text: e.target.value })}
                                onBlur={() => patchSource({ source_text: source.source_text })}
                                className="rounded-sm"
                            />
                        </Field>
                    </div>
                    <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Field label="Key points (optional)">
                            <Textarea
                                data-testid="edit-keypoints"
                                rows={3}
                                value={source.key_points || ""}
                                onChange={e => setSource({ ...source, key_points: e.target.value })}
                                onBlur={() => patchSource({ key_points: source.key_points })}
                                className="rounded-sm"
                            />
                        </Field>
                        <Field label={<>Source notes <span className="text-[9px] text-accent italic normal-case tracking-normal">internal only — never quoted in output</span></>}>
                            <Textarea
                                data-testid="edit-notes"
                                rows={3}
                                value={source.source_notes || ""}
                                onChange={e => setSource({ ...source, source_notes: e.target.value })}
                                onBlur={() => patchSource({ source_notes: source.source_notes })}
                                placeholder="Direction for the AI that should inform the output but not appear in it."
                                className="rounded-sm"
                            />
                        </Field>
                    </div>
                    {saving && <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mt-3">Saving…</p>}
                </section>

                {/* --- Asset Analysis Engine --- */}
                <section className="border border-border bg-card rounded-sm p-6 mb-6" data-testid="analysis-card">
                    <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
                        <div>
                            <h3 className="font-display text-lg font-bold leading-tight flex items-center gap-2">
                                <Brain size={18} weight="duotone" className="text-primary" />
                                Asset Analysis
                            </h3>
                            <p className="text-xs text-muted-foreground mt-1 max-w-[56ch]">
                                Before generating, let HAEVN&apos;s strategist read the source and recommend which asset types it actually has the substance to support. Refuses to recommend if context is thin.
                            </p>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                            <Button data-testid="analyze-btn" variant="outline" size="sm" className="rounded-sm" onClick={runAnalysis} disabled={analyzing}>
                                <Brain size={14} weight="duotone" className="mr-1.5" />
                                {analyzing ? "Analyzing…" : "Run analysis"}
                            </Button>
                            <Button data-testid="auto-pipeline-btn" variant="outline" size="sm" className="rounded-sm" onClick={runAutoPipeline} disabled={autoRunning}>
                                <Sparkle size={14} weight="duotone" className="mr-1.5" />
                                {autoRunning ? "Running pipeline…" : "Auto-pipeline"}
                            </Button>
                            <Button data-testid="ship-source-btn" size="sm" className="rounded-sm" onClick={() => setShipDialogOpen(true)} disabled={shipBusy}>
                                <PaperPlaneTilt size={14} weight="fill" className="mr-1.5" />
                                {shipBusy ? "Shipping…" : "Ship to Buffer"}
                            </Button>
                        </div>
                    </div>
                    {analysisResult?.insufficient_context && (
                        <div data-testid="insufficient-warning" className="border border-accent/30 bg-accent/5 rounded-sm p-4 flex items-start gap-3">
                            <Warning size={18} weight="duotone" className="text-accent shrink-0 mt-0.5" />
                            <p className="text-sm text-foreground">{analysisResult.warning}</p>
                        </div>
                    )}
                    {analysisResult?.recommendations?.length > 0 && (
                        <div className="space-y-2" data-testid="analysis-recommendations">
                            {analysisResult.visual_style && (
                                <p className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground mb-2">
                                    Suggested visual style: <span className="text-primary">{analysisResult.visual_style}</span>
                                </p>
                            )}
                            {analysisResult.recommendations.map(r => {
                                const meta = ASSET_META[r.asset_type] || { label: r.asset_type, Icon: FileText };
                                const Icon = meta.Icon;
                                return (
                                    <div key={r.asset_type} className={`flex items-start gap-3 p-3 border rounded-sm ${r.recommended ? "border-primary/30 bg-primary/5" : "border-border bg-muted/20 opacity-60"}`}>
                                        <Icon size={16} weight="duotone" className={r.recommended ? "text-primary mt-0.5" : "text-muted-foreground mt-0.5"} />
                                        <div className="flex-1">
                                            <p className="font-display font-semibold text-sm">{meta.label}</p>
                                            <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{r.reason}</p>
                                        </div>
                                        {r.recommended && (
                                            <Badge variant="outline" className="rounded-sm text-[9px] font-mono">RECOMMENDED</Badge>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </section>

                {/* --- Generate assets --- */}
                <section className="border border-primary/20 bg-primary/5 rounded-sm p-6 mb-8" data-testid="generate-assets-card">
                    <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
                        <div>
                            <h3 className="font-display text-lg font-bold leading-tight">Generate content assets</h3>
                            <p className="text-xs text-muted-foreground mt-1 max-w-[56ch]">
                                Choose which platforms you need. Each one is generated in parallel from the same source — structured output, no concatenated blobs.
                            </p>
                        </div>
                        <div className="flex items-center gap-2">
                            <Select value={goalOverride || "default"} onValueChange={v => setGoalOverride(v === "default" ? "" : v)}>
                                <SelectTrigger data-testid="goal-override" className="rounded-sm h-9 w-56 text-xs"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="default">Use source default goal</SelectItem>
                                    {opts.content_goals.map(g => (<SelectItem key={g} value={g}>Override: {g}</SelectItem>))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4" data-testid="platform-picker">
                        {opts.asset_types.map(p => {
                            const meta = ASSET_META[p] || { label: p, Icon: FileText };
                            const Icon = meta.Icon;
                            const active = selectedPlatforms.has(p);
                            return (
                                <button
                                    key={p}
                                    type="button"
                                    data-testid={`platform-${p}`}
                                    onClick={() => togglePlatform(p)}
                                    className={`flex items-center gap-2 px-3 py-2.5 border rounded-sm text-sm transition-colors ${
                                        active ? "border-primary bg-primary text-primary-foreground" : "border-border bg-background hover:border-primary/40"
                                    }`}
                                >
                                    <Icon size={14} weight={active ? "fill" : "duotone"} />
                                    <span className="font-display font-semibold">{meta.label}</span>
                                </button>
                            );
                        })}
                    </div>
                    <div className="flex items-center justify-between gap-3">
                        <p className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">
                            {selectedPlatforms.size} selected · model GPT-4o · HAEVN voice
                        </p>
                        <Button
                            data-testid="generate-btn"
                            onClick={generate}
                            disabled={generating || selectedPlatforms.size === 0}
                            className="rounded-sm"
                        >
                            <Sparkle size={14} weight="duotone" className="mr-1.5" />
                            {generating ? "Generating…" : "Generate selected assets"}
                        </Button>
                    </div>
                </section>

                {/* --- Generated assets --- */}
                <section data-testid="assets-section">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="font-display text-lg font-bold">Generated assets</h3>
                        <span className="font-mono text-xs text-muted-foreground tabular-nums">{assets.length} total</span>
                    </div>
                    {assets.length === 0 ? (
                        <div className="border border-dashed border-border rounded-sm p-10 text-center" data-testid="no-assets">
                            <p className="font-display text-sm font-semibold text-muted-foreground">No assets generated yet</p>
                            <p className="text-xs text-muted-foreground mt-1">Select one or more platforms above and click &ldquo;Generate selected assets&rdquo;.</p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {assets.map(a => (
                                <AssetCard
                                    key={`${a.id}-${a.updated_at || ""}`}
                                    asset={a}
                                    statuses={opts.asset_statuses || []}
                                    onUpdate={(patch) => updateAsset(a.id, patch)}
                                    onDelete={() => deleteAsset(a.id)}
                                    onTransition={(s) => transition(a.id, s)}
                                    onQueue={() => queueToBuffer(a.id)}
                                    onSchedule={() => setScheduleDialog(a)}
                                    onRevalidate={() => revalidate(a.id)}
                                />
                            ))}
                        </div>
                    )}
                </section>
            </PageBody>

            {/* V2 — Schedule dialog */}
            <Dialog open={!!scheduleDialog} onOpenChange={(o) => !o && setScheduleDialog(null)}>
                <DialogContent className="rounded-sm max-w-md" data-testid="schedule-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display">Schedule to Buffer</DialogTitle>
                        <DialogDescription className="text-[12px]">
                            Pick a UTC date and time. Buffer will publish this asset at that exact moment on the configured channel for its platform.
                        </DialogDescription>
                    </DialogHeader>
                    {scheduleDialog && (
                        <ScheduleForm
                            asset={scheduleDialog}
                            onClose={() => setScheduleDialog(null)}
                            onScheduled={reload}
                        />
                    )}
                </DialogContent>
            </Dialog>

            <ShipPlatformDialog
                open={shipDialogOpen}
                onOpenChange={setShipDialogOpen}
                onConfirm={shipSourceToBuffer}
                title={source?.title}
                contentType={null}
                busy={shipBusy}
            />
        </>
    );
}

function ScheduleForm({ asset, onClose, onScheduled }) {
    // default = tomorrow 9am local (converted to UTC ISO)
    const tomorrow9 = (() => {
        const d = new Date();
        d.setDate(d.getDate() + 1);
        d.setHours(9, 0, 0, 0);
        // Format yyyy-MM-ddTHH:mm for datetime-local input
        const pad = n => String(n).padStart(2, "0");
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    })();
    const [when, setWhen] = useState(tomorrow9);
    const [busy, setBusy] = useState(false);

    const submit = async (overrideReason = null) => {
        if (!when) return;
        setBusy(true);
        try {
            // datetime-local is local time; convert to UTC ISO
            const local = new Date(when);
            const iso = local.toISOString();
            const body = overrideReason
                ? { scheduled_for: iso, override_reason: overrideReason }
                : { scheduled_for: iso };
            await api.post(`/content-studio/content/${asset.id}/schedule`, body);
            toast.success(`Scheduled for ${local.toLocaleString()}${overrideReason ? " (override)" : ""}`);
            onClose();
            await onScheduled();
        } catch (e) {
            const status = e?.response?.status;
            if (status === 409 && !overrideReason) {
                const reason = window.prompt(
                    "This asset has unsupported claims flagged by the fact validator. "
                    + "Provide an override reason to schedule it anyway:",
                );
                if (reason && reason.trim()) {
                    return submit(reason.trim());
                }
                toast.error("Schedule cancelled — override reason required.");
            } else {
                toast.error(e?.response?.data?.detail || "Schedule failed");
            }
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="space-y-4">
            <div>
                <label className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground block mb-1.5">When (local time)</label>
                <input
                    type="datetime-local"
                    data-testid="schedule-when"
                    value={when}
                    onChange={(e) => setWhen(e.target.value)}
                    className="w-full border border-border rounded-sm h-9 px-3 text-sm bg-background"
                />
            </div>
            <DialogFooter>
                <Button variant="ghost" size="sm" className="rounded-sm" onClick={onClose}>Cancel</Button>
                <Button data-testid="schedule-confirm" size="sm" className="rounded-sm" onClick={() => submit()} disabled={busy}>
                    <Calendar size={12} className="mr-1.5" />
                    {busy ? "Scheduling…" : "Schedule"}
                </Button>
            </DialogFooter>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <label className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground block mb-1.5">{label}</label>
            {children}
        </div>
    );
}

// ----------------- Per-asset structured editors -----------------

function ValidationPanel({ asset, onRevalidate }) {
    const status = asset.validation_status || "skipped";
    const issues = asset.validation_issues || [];
    const meta = (() => {
        if (status === "pass") return {
            tone: "border-emerald-500/40 bg-emerald-500/5",
            Icon: ShieldCheck, label: "Fact-check: PASS",
            sub: `Validated against KB v${asset.validation_kb_version ?? "?"}`,
            iconTone: "text-emerald-600 dark:text-emerald-300",
        };
        if (status === "needs_review") return {
            tone: "border-red-500/50 bg-red-500/5",
            Icon: ShieldWarning, label: `Fact-check: NEEDS REVIEW (${issues.length})`,
            sub: `Validated against KB v${asset.validation_kb_version ?? "?"}`,
            iconTone: "text-red-600 dark:text-red-300",
        };
        if (status === "error") return {
            tone: "border-amber-500/40 bg-amber-500/5",
            Icon: Warning, label: "Fact-check: VALIDATOR ERROR",
            sub: "Re-validate to retry.",
            iconTone: "text-amber-600 dark:text-amber-300",
        };
        return {
            tone: "border-border bg-muted/30",
            Icon: ShieldSlash, label: "Fact-check: NOT VALIDATED",
            sub: "Upload a Knowledge Base document under Settings → Knowledge Base to enable validation.",
            iconTone: "text-muted-foreground",
        };
    })();
    const Icon = meta.Icon;
    return (
        <div className={`border rounded-sm p-3 mb-4 ${meta.tone}`} data-testid={`validation-panel-${asset.id}`}>
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                    <Icon size={14} weight="duotone" className={meta.iconTone} />
                    <div>
                        <p className="font-mono text-[10px] uppercase tracking-wider font-bold" data-testid={`validation-status-${asset.id}`}>
                            {meta.label}
                        </p>
                        <p className="text-[11px] text-muted-foreground mt-0.5">{meta.sub}</p>
                    </div>
                </div>
                <Button
                    variant="outline" size="sm"
                    className="rounded-sm h-7 px-2 text-[11px]"
                    onClick={onRevalidate}
                    data-testid={`revalidate-${asset.id}`}
                >
                    <ArrowsClockwise size={11} className="mr-1" /> Re-validate
                </Button>
            </div>
            {asset.validation_override_by && (
                <p className="mt-2 text-[11px] text-amber-700 dark:text-amber-300">
                    Override on file — approved by {asset.validation_override_by}: &ldquo;{asset.validation_override_reason}&rdquo;
                </p>
            )}
            {issues.length > 0 && (
                <ul className="mt-3 space-y-2" data-testid={`validation-issues-${asset.id}`}>
                    {issues.map((iss, i) => (
                        <li key={i} className="border border-red-500/30 bg-background rounded-sm p-2 text-[12px]">
                            <div className="flex items-center gap-2 mb-1">
                                <span className="font-mono text-[9px] uppercase tracking-wider text-red-700 dark:text-red-300 bg-red-500/10 px-1.5 py-0.5 rounded-sm">
                                    {iss.category || "issue"}
                                </span>
                                {iss.severity && (
                                    <span className="font-mono text-[9px] uppercase tracking-wider text-muted-foreground">
                                        · {iss.severity}
                                    </span>
                                )}
                                {iss.field && (
                                    <span className="font-mono text-[9px] uppercase tracking-wider text-muted-foreground">
                                        · {iss.field}
                                    </span>
                                )}
                            </div>
                            {iss.claim && (
                                <p className="italic text-foreground/90 mb-1">&ldquo;{iss.claim}&rdquo;</p>
                            )}
                            {iss.reason && (
                                <p className="text-muted-foreground">{iss.reason}</p>
                            )}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

function AssetCard({ asset, statuses, onUpdate, onDelete, onTransition, onQueue, onSchedule, onRevalidate }) {
    const type = asset.content_type || asset.platform || "Asset";
    const meta = ASSET_META[type] || { label: type, Icon: FileText };
    const Icon = meta.Icon;
    // Local optimistic draft — the key prop above re-mounts this card
    // whenever the parent receives an updated asset, so we can safely
    // initialise from props without a setState-in-effect dance.
    const [draft, setDraft] = useState(asset);

    const save = (patch) => {
        setDraft(d => ({ ...d, ...patch }));
        onUpdate(patch);
    };

    // Workflow status — derive next available transitions.
    const ALLOWED = {
        Draft:     ["Review", "Approved"],
        Review:    ["Approved", "Draft"],
        Approved:  ["Scheduled", "Published", "Draft"],
        Scheduled: ["Published"],
        Published: [],
        Archived:  ["Draft"],
    };
    const current = asset.status || "Draft";
    const nextActions = ALLOWED[current] || [];
    const apiBase = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

    return (
        <article className="border border-border bg-card rounded-sm p-5" data-testid={`asset-card-${asset.id}`}>
            <header className="flex items-start justify-between mb-4 pb-3 border-b border-border gap-3 flex-wrap">
                <div className="flex items-center gap-3 flex-wrap">
                    <Icon size={16} weight="duotone" className="text-primary" />
                    <span className="font-display font-bold text-sm">{meta.label}</span>
                    {asset.content_goal && <Badge variant="outline" className="rounded-sm text-[10px] font-mono">{asset.content_goal}</Badge>}
                    <Badge
                        variant="outline"
                        className={`rounded-sm text-[10px] font-mono ${
                            current === "Approved" ? "border-primary text-primary" :
                            current === "Scheduled" ? "border-accent text-accent" :
                            current === "Published" ? "border-green-600 text-green-700" : ""
                        }`}
                        data-testid={`asset-status-${asset.id}`}
                    >
                        {current.toUpperCase()}
                    </Badge>
                </div>
                <div className="flex items-center gap-1.5 flex-wrap">
                    {nextActions.map(s => (
                        <Button
                            key={s}
                            data-testid={`transition-${s.toLowerCase()}-${asset.id}`}
                            variant="outline" size="sm" className="rounded-sm h-7 px-2 text-[11px]"
                            onClick={() => onTransition(s)}
                        >
                            {s === "Review"    && <ArrowRight size={11} className="mr-1" />}
                            {s === "Approved"  && <Check size={11} className="mr-1" />}
                            {s}
                        </Button>
                    ))}
                    {current === "Approved" && (
                        <>
                            <Button data-testid={`schedule-${asset.id}`} variant="outline" size="sm" className="rounded-sm h-7 px-2 text-[11px]" onClick={onSchedule}>
                                <Calendar size={11} className="mr-1" /> Schedule
                            </Button>
                            <Button data-testid={`queue-${asset.id}`} variant="outline" size="sm" className="rounded-sm h-7 px-2 text-[11px]" onClick={onQueue}>
                                <PaperPlaneTilt size={11} className="mr-1" /> Queue
                            </Button>
                        </>
                    )}
                    <Button data-testid={`delete-asset-${asset.id}`} variant="ghost" size="sm" className="rounded-sm h-7 w-7 p-0" onClick={onDelete} title="Delete asset">
                        <Trash size={12} />
                    </Button>
                </div>
            </header>

            <ValidationPanel asset={asset} onRevalidate={onRevalidate} />

            {/* Visual preview for rendered template assets */}
            {asset.rendered_image_path && (
                <div className="mb-4 border border-border rounded-sm overflow-hidden bg-muted/30 max-w-md mx-auto">
                    <img
                        data-testid={`rendered-preview-${asset.id}`}
                        src={`${apiBase}/content-studio/rendered/${asset.rendered_image_path}`}
                        alt="rendered template"
                        className="w-full h-auto block"
                    />
                </div>
            )}

            {type === "Instagram"    && <InstagramEditor    draft={draft} save={save} />}
            {type === "Facebook"     && <BodyAndCtaEditor   draft={draft} save={save} bodyKey="post_body" ctaKey="cta" />}
            {type === "LinkedIn"     && <BodyAndCtaEditor   draft={draft} save={save} bodyKey="post_body" ctaKey="cta" ctaLabel="Conversation prompt" />}
            {type === "Threads"      && <ThreadsEditor      draft={draft} save={save} />}
            {type === "Carousel"     && <CarouselEditor     draft={draft} save={save} />}
            {type === "VideoScript"  && <VideoScriptEditor  draft={draft} save={save} />}
            {type === "YouTube"      && <YouTubeEditor      draft={draft} save={save} />}
            {type === "Blog"         && <BlogEditor         draft={draft} save={save} />}
            {type === "Email"        && <EmailEditor        draft={draft} save={save} />}
            {type === "QuoteGraphic" && <QuoteGraphicEditor draft={draft} save={save} />}
            {type === "TextGraphic"  && <TextGraphicEditor  draft={draft} save={save} />}
        </article>
    );
}

function InstagramEditor({ draft, save }) {
    return (
        <div className="space-y-3">
            <Field label="Caption">
                <Textarea data-testid="ig-caption" rows={5} value={draft.ig_caption || ""} onChange={e => save({ ig_caption: e.target.value })} className="rounded-sm" />
            </Field>
            <Field label="CTA">
                <Input data-testid="ig-cta" value={draft.ig_cta || ""} onChange={e => save({ ig_cta: e.target.value })} className="rounded-sm" />
            </Field>
            <Field label="Hashtags">
                <ChipList values={draft.ig_hashtags || []} onChange={v => save({ ig_hashtags: v })} prefix="#" testid="ig-hashtags" />
            </Field>
        </div>
    );
}

function BodyAndCtaEditor({ draft, save, bodyKey, ctaKey, ctaLabel = "CTA" }) {
    return (
        <div className="space-y-3">
            <Field label="Post body">
                <Textarea data-testid={`${bodyKey}-input`} rows={7} value={draft[bodyKey] || ""} onChange={e => save({ [bodyKey]: e.target.value })} className="rounded-sm" />
            </Field>
            <Field label={ctaLabel}>
                <Input data-testid={`${ctaKey}-input`} value={draft[ctaKey] || ""} onChange={e => save({ [ctaKey]: e.target.value })} className="rounded-sm" />
            </Field>
        </div>
    );
}

function ThreadsEditor({ draft, save }) {
    const replies = draft.threads_replies || [];
    return (
        <div className="space-y-3">
            <Field label="Lead post (≤280 chars)">
                <Textarea data-testid="threads-body" rows={3} value={draft.threads_body || ""} onChange={e => save({ threads_body: e.target.value })} className="rounded-sm" />
            </Field>
            <Field label="Reply chain">
                <div className="space-y-2" data-testid="threads-replies">
                    {replies.map((r, i) => (
                        <div key={i} className="flex items-start gap-2">
                            <span className="font-mono text-[10px] text-primary mt-2 tabular-nums">{String(i + 1).padStart(2, "0")}</span>
                            <Textarea
                                rows={2}
                                value={r}
                                onChange={e => {
                                    const next = [...replies];
                                    next[i] = e.target.value;
                                    save({ threads_replies: next });
                                }}
                                className="rounded-sm flex-1"
                            />
                            <Button variant="ghost" size="sm" className="rounded-sm h-7 w-7 p-0" onClick={() => save({ threads_replies: replies.filter((_, j) => j !== i) })}>
                                <X size={12} />
                            </Button>
                        </div>
                    ))}
                    <Button variant="outline" size="sm" className="rounded-sm h-7 text-[11px]" onClick={() => save({ threads_replies: [...replies, ""] })}>
                        <Plus size={12} className="mr-1" /> Add reply
                    </Button>
                </div>
            </Field>
        </div>
    );
}

function CarouselEditor({ draft, save }) {
    const slides = draft.slides || [];
    const updateSlide = (i, v) => {
        const next = [...slides];
        next[i] = v;
        save({ slides: next });
    };
    const moveSlide = (i, dir) => {
        const j = i + dir;
        if (j < 0 || j >= slides.length) return;
        const next = [...slides];
        [next[i], next[j]] = [next[j], next[i]];
        save({ slides: next });
    };
    return (
        <div className="space-y-2" data-testid="carousel-slides">
            {slides.map((s, i) => (
                <div key={i} className="flex items-start gap-2 border border-border rounded-sm p-2 bg-background">
                    <span className="font-mono text-[11px] text-primary tabular-nums mt-2 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                    <Textarea
                        data-testid={`slide-${i}`}
                        rows={2}
                        value={s}
                        onChange={e => updateSlide(i, e.target.value)}
                        className="rounded-sm flex-1 text-sm"
                    />
                    <div className="flex flex-col gap-0.5">
                        <button onClick={() => moveSlide(i, -1)} className="text-[10px] text-muted-foreground hover:text-primary px-1" title="Move up">▲</button>
                        <button onClick={() => moveSlide(i, 1)} className="text-[10px] text-muted-foreground hover:text-primary px-1" title="Move down">▼</button>
                    </div>
                    <Button variant="ghost" size="sm" className="rounded-sm h-7 w-7 p-0 shrink-0" onClick={() => save({ slides: slides.filter((_, j) => j !== i) })}>
                        <X size={12} />
                    </Button>
                </div>
            ))}
            <Button variant="outline" size="sm" className="rounded-sm h-7 text-[11px]" onClick={() => save({ slides: [...slides, ""] })}>
                <Plus size={12} className="mr-1" /> Add slide
            </Button>
        </div>
    );
}

function VideoScriptEditor({ draft, save }) {
    return (
        <div className="space-y-3">
            <Field label="Hook">
                <Input data-testid="vs-hook" value={draft.script_hook || ""} onChange={e => save({ script_hook: e.target.value })} className="rounded-sm" />
            </Field>
            <Field label="Body">
                <Textarea data-testid="vs-body" rows={7} value={draft.script_body || ""} onChange={e => save({ script_body: e.target.value })} className="rounded-sm" />
            </Field>
            <Field label="CTA">
                <Input data-testid="vs-cta" value={draft.script_cta || ""} onChange={e => save({ script_cta: e.target.value })} className="rounded-sm" />
            </Field>
        </div>
    );
}

function YouTubeEditor({ draft, save }) {
    return (
        <div className="space-y-3">
            <Field label="Title (≤70 chars)">
                <Input data-testid="yt-title" value={draft.yt_title || ""} onChange={e => save({ yt_title: e.target.value })} className="rounded-sm" />
            </Field>
            <Field label="Description">
                <Textarea data-testid="yt-desc" rows={6} value={draft.yt_description || ""} onChange={e => save({ yt_description: e.target.value })} className="rounded-sm" />
            </Field>
            <Field label="Tags">
                <ChipList values={draft.yt_tags || []} onChange={v => save({ yt_tags: v })} testid="yt-tags" />
            </Field>
            <Field label="Chapters (mm:ss Title)">
                <ChipList values={draft.youtube_chapters || []} onChange={v => save({ youtube_chapters: v })} testid="yt-chapters" placeholder="00:30 Chapter title" />
            </Field>
        </div>
    );
}

function BlogEditor({ draft, save }) {
    return (
        <div className="space-y-3">
            <Field label="Title">
                <Input data-testid="blog-title" value={draft.blog_title || ""} onChange={e => save({ blog_title: e.target.value })} className="rounded-sm" />
            </Field>
            <Field label="Body (markdown allowed)">
                <Textarea data-testid="blog-body" rows={14} value={draft.blog_body || ""} onChange={e => save({ blog_body: e.target.value })} className="rounded-sm font-mono text-xs" />
            </Field>
        </div>
    );
}

function EmailEditor({ draft, save }) {
    return (
        <div className="space-y-3">
            <Field label="Subject">
                <Input data-testid="email-subject" value={draft.email_subject || ""} onChange={e => save({ email_subject: e.target.value })} className="rounded-sm" />
            </Field>
            <Field label="Body">
                <Textarea data-testid="email-body" rows={12} value={draft.email_body || ""} onChange={e => save({ email_body: e.target.value })} className="rounded-sm" />
            </Field>
        </div>
    );
}

function QuoteGraphicEditor({ draft, save }) {
    return (
        <div className="space-y-3">
            <Field label="Quote">
                <Textarea data-testid="quote-text" rows={3} value={draft.quote_text || ""} onChange={e => save({ quote_text: e.target.value })} className="rounded-sm" />
            </Field>
            <Field label="Attribution">
                <Input data-testid="quote-attribution" value={draft.quote_attribution || ""} onChange={e => save({ quote_attribution: e.target.value })} className="rounded-sm" />
            </Field>
            <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Image is auto-rendered from the brand profile. Re-render on save (coming soon).</p>
        </div>
    );
}

function TextGraphicEditor({ draft, save }) {
    return (
        <div className="space-y-3">
            <Field label="Statement">
                <Textarea data-testid="text-graphic-statement" rows={3} value={draft.text_graphic_statement || ""} onChange={e => save({ text_graphic_statement: e.target.value })} className="rounded-sm" />
            </Field>
        </div>
    );
}

// Lightweight chip-list input — Enter to add, X to remove.
function ChipList({ values, onChange, prefix = "", placeholder = "Type and press Enter", testid }) {
    const [input, setInput] = useState("");
    const add = () => {
        const v = input.trim().replace(/^#/, "");
        if (!v) return;
        if (!values.includes(v)) onChange([...values, v]);
        setInput("");
    };
    return (
        <div className="flex flex-wrap gap-1.5 items-center" data-testid={testid}>
            {values.map((v, i) => (
                <span key={i} className="inline-flex items-center gap-1 text-[11px] font-mono bg-muted px-2 py-0.5 rounded-sm">
                    {prefix}{v}
                    <button onClick={() => onChange(values.filter((_, j) => j !== i))} className="text-muted-foreground hover:text-accent">
                        <X size={10} />
                    </button>
                </span>
            ))}
            <input
                type="text"
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
                onBlur={add}
                placeholder={placeholder}
                className="text-xs px-1.5 py-0.5 border-b border-transparent focus:border-primary outline-none bg-transparent min-w-[120px]"
            />
        </div>
    );
}
